import { FC, useState } from 'react'

interface TmsPageIntroCardProps {
    title: string
    description: string
    /** YouTube video ID — used when videoUrl is not provided */
    videoId?: string
    /** Arcade (or other) embed URL — takes priority over videoId */
    videoUrl?: string
    onDismiss: () => void
}

export const TmsPageIntroCard: FC<TmsPageIntroCardProps> = ({ title, description, videoId, videoUrl, onDismiss }) => {
    const [playing, setPlaying] = useState(false)

    return (
        <div
            className="fixed bottom-6 right-6 z-[9990] w-80 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
            style={{ animation: 'extFadeInUp 0.25s ease forwards' }}
        >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Tutorial</span>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 dark:text-slate-500 dark:hover:bg-slate-800 transition-colors"
                    aria-label="Dismiss"
                >
                    <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
                        <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
                    </svg>
                </button>
            </div>

            {/* Video */}
            <div className="mx-4 mt-3 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 relative" style={{ aspectRatio: '16/9' }}>
                {videoUrl ? (
                    <iframe
                        src={videoUrl}
                        className="absolute inset-0 w-full h-full border-0"
                        loading="lazy"
                        allowFullScreen
                        allow="fullscreen; clipboard-write"
                        title={title}
                    />
                ) : playing ? (
                    <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                        className="absolute inset-0 w-full h-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={title}
                    />
                ) : (
                    <div className="absolute inset-0 cursor-pointer" onClick={() => setPlaying(true)}>
                        <img src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} alt={title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-md">
                                <svg width={14} height={14} viewBox="0 0 10 10" fill="none">
                                    <path d="M2 1.5l7 3.5-7 3.5V1.5z" fill="#1a1a1a" />
                                </svg>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-4">
                <h3 className="text-[15px] font-bold text-slate-900 dark:text-slate-100 mb-1.5 leading-snug m-0">{title}</h3>
                <p className="text-[13px] text-slate-500 dark:text-slate-400 leading-relaxed m-0">{description}</p>
            </div>
        </div>
    )
}
